import { z } from "zod";
import type { StoneRequestMode } from "@gull/shared";
import { MAX_ITEMS, MAX_ORDER_IMAGES } from "./constants";

const DRAFT_VERSION = 1;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

const draftSchema = z.object({
  version: z.literal(DRAFT_VERSION),
  items: z
    .array(
      z.object({
        description: z.string().max(200),
        source: z.enum(["manual", "ai"]).optional(),
      }),
    )
    .max(MAX_ITEMS),
  stoneMode: z.enum(["NONE_OR_KEEP", "REMOVE_ALL", "REMOVE_ALL_GOLDSMITH", "SPECIFIC"]),
  selectedStoneItemIndexes: z.array(z.number().int().nonnegative()),
  orderImagePaths: z.array(z.string().max(500)).max(MAX_ORDER_IMAGES),
  activeStep: z.number().int().min(0).default(0),
  savedAt: z.number(),
});

type MailOrderDraft = z.infer<typeof draftSchema>;
export type MailOrderDraftInput = Omit<MailOrderDraft, "version" | "savedAt">;
/** A draft as it comes back: an answer the form no longer offers has been translated. */
type RestoredMailOrderDraft = Omit<MailOrderDraft, "stoneMode"> & { stoneMode: StoneRequestMode };

const keyFor = (uid: string) => `gull:bestill-pose:draft:${uid}`;

export function clearDraft(uid: string) {
  try {
    window.localStorage.removeItem(keyFor(uid));
  } catch {
    return;
  }
}

export function readDraft(uid: string): RestoredMailOrderDraft | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(keyFor(uid));
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearDraft(uid);
    return null;
  }

  const result = draftSchema.safeParse(parsed);
  if (!result.success || Date.now() - result.data.savedAt > MAX_AGE_MS) {
    clearDraft(uid);
    return null;
  }
  return withoutRemoveAll(result.data);
}

/**
 * Removal is chosen per item now. A draft saved while customers could pick "all items" comes back
 * as every item selected, which asks for the same thing.
 */
function withoutRemoveAll(draft: MailOrderDraft): RestoredMailOrderDraft {
  if (draft.stoneMode === "NONE_OR_KEEP" || draft.stoneMode === "SPECIFIC") {
    return { ...draft, stoneMode: draft.stoneMode };
  }
  return {
    ...draft,
    stoneMode: "SPECIFIC",
    selectedStoneItemIndexes: draft.items.map((_, index) => index),
  };
}

export function writeDraft(uid: string, draft: MailOrderDraftInput) {
  try {
    window.localStorage.setItem(
      keyFor(uid),
      JSON.stringify({ ...draft, version: DRAFT_VERSION, savedAt: Date.now() }),
    );
  } catch {
    return;
  }
}

export function isEmptyDraft(draft: MailOrderDraftInput) {
  return (
    draft.items.every((item) => !item.description.trim()) &&
    draft.stoneMode === "NONE_OR_KEEP" &&
    draft.orderImagePaths.length === 0
  );
}
